import { API_BASE_URL } from 'utils/axios';

export interface TelegramAuthResponse {
  status: string;
  message?: string;
  api_key?: string;
  user_id?: number;
  is_new_user?: boolean;
  telegram_data?: {
    telegram_user_id: number;
    username?: string;
    first_name?: string;
    photo_url?: string;
  };
}

/* ------------------------------------------------------------------------- *
 * Telegram Login (OpenID Connect popup).
 *
 * Spec: specs/active/2026-10-27-telegram-oidc-login (plan Step 8).
 * Flow: the API mints a single-use nonce (and binds it to this browser with an
 * HttpOnly cookie) -> Telegram's popup library returns a signed `id_token`
 * carrying that nonce -> the API verifies the token and opens the same session
 * as every other sign-in method. Builds without REACT_APP_TELEGRAM_LOGIN_CLIENT_ID
 * show no Telegram button (the legacy widget path was retired, Step 12).
 * ------------------------------------------------------------------------- */

export const TELEGRAM_LOGIN_SCRIPT_URL =
  'https://oauth.telegram.org/js/telegram-login.js?6';
const TELEGRAM_LOGIN_SCRIPT_ID = 'telegram-login-oidc-library';
const TELEGRAM_LOGIN_LOAD_TIMEOUT_MS = 15000;

/** A non-2xx answer from the API, with its status kept for the caller. */
export class TelegramOidcError extends Error {
  status?: number;
  detail?: string;

  constructor(message: string, status?: number, detail?: string) {
    super(message);
    this.name = 'TelegramOidcError';
    this.status = status;
    this.detail = detail;
  }
}

const readDetail = async (response: Response): Promise<string | undefined> => {
  try {
    const body = await response.json();
    return typeof body?.detail === 'string' ? body.detail : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Ask the API for a single-use login nonce. `credentials: 'include'` so the
 * nonce cookie the API sets lands in the browser; the login call must carry it.
 * The API requires `Content-Type: application/json` here too (415 otherwise):
 * a cross-site form post cannot set it, so it forces a CORS preflight.
 */
export const fetchTelegramOidcNonce = async (): Promise<string> => {
  const response = await fetch(`${API_BASE_URL}/auth/telegram/oidc/nonce`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: '{}',
  });
  if (!response.ok) {
    throw new TelegramOidcError(
      (await readDetail(response)) || `Nonce request failed (${response.status})`,
      response.status
    );
  }
  const data = await response.json();
  if (typeof data?.nonce !== 'string' || !data.nonce) {
    throw new TelegramOidcError('Nonce response carried no nonce', response.status);
  }
  return data.nonce;
};

let libraryPromise: Promise<void> | null = null;

const hasLoginAuth = () => typeof window.Telegram?.Login?.auth === 'function';

/**
 * Inject Telegram's login library once. Resolves when `Telegram.Login.auth`
 * exists; rejects on a load error, a timeout, or a script that loaded without
 * defining it. A failed load is forgotten, so a later call retries.
 */
export const loadTelegramLoginLibrary = (): Promise<void> => {
  if (libraryPromise) return libraryPromise;

  libraryPromise = new Promise<void>((resolve, reject) => {
    const existing = document.getElementById(TELEGRAM_LOGIN_SCRIPT_ID);
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = TELEGRAM_LOGIN_SCRIPT_ID;
    script.src = TELEGRAM_LOGIN_SCRIPT_URL;
    script.async = true;

    const timer = window.setTimeout(() => {
      fail(new Error('Telegram login library timed out'));
    }, TELEGRAM_LOGIN_LOAD_TIMEOUT_MS);

    function fail(error: Error) {
      window.clearTimeout(timer);
      script.onload = null;
      script.onerror = null;
      libraryPromise = null;
      reject(error);
    }

    script.onload = () => {
      if (!hasLoginAuth()) {
        fail(new Error('Telegram login library loaded without Telegram.Login.auth'));
        return;
      }
      window.clearTimeout(timer);
      resolve();
    };
    script.onerror = () => fail(new Error('Telegram login library failed to load'));

    document.head.appendChild(script);
  });

  return libraryPromise;
};

/** Test seam: forget the memoised library load. */
export const resetTelegramLoginLibraryForTests = () => {
  libraryPromise = null;
};

export type TelegramIdTokenResult =
  | { kind: 'token'; idToken: string; user?: TelegramLoginUser }
  | { kind: 'cancelled' };

/** The callback's `error` values that mean "the user closed the popup". The
 *  exact strings are not documented; anything else with a message is a
 *  failure, a `user` without a token is a failure, and a result with no
 *  token, no error and no user is a cancel. */
const CANCEL_ERROR = /cancel|close|dismiss/i;

/**
 * Open Telegram's popup. MUST be called synchronously from the click handler
 * (the Promise executor runs synchronously): a popup opened after an `await`
 * is blocked by browsers. The library must already be loaded.
 */
export const requestTelegramIdToken = (
  options: TelegramLoginAuthOptions
): Promise<TelegramIdTokenResult> =>
  new Promise((resolve, reject) => {
    const auth = window.Telegram?.Login?.auth;
    if (typeof auth !== 'function') {
      reject(new Error('Telegram login library is not loaded'));
      return;
    }
    auth(options, (result) => {
      if (result && typeof result.id_token === 'string' && result.id_token) {
        resolve({ kind: 'token', idToken: result.id_token, user: result.user });
        return;
      }
      const error = result && typeof result.error === 'string' ? result.error : '';
      if (error && CANCEL_ERROR.test(error)) {
        resolve({ kind: 'cancelled' });
        return;
      }
      if (error) {
        reject(new TelegramOidcError(error));
        return;
      }
      // A profile without a token is a broken integration (e.g. a scope the
      // library needs), not a user closing the popup: report it.
      if (result && result.user) {
        reject(new TelegramOidcError('Telegram returned a profile without an id_token'));
        return;
      }
      resolve({ kind: 'cancelled' });
    });
  });

/**
 * Exchange an `id_token` for a site session. Any non-2xx is a failure
 * (401 = refused, 415 = not JSON, 429 = rate-limited, 503 = unavailable).
 * The API requires `Content-Type: application/json` on this call.
 */
export const telegramOidcLogin = async (
  idToken: string
): Promise<TelegramAuthResponse> => {
  const response = await fetch(`${API_BASE_URL}/auth/telegram/oidc/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id_token: idToken }),
  });
  if (!response.ok) {
    throw new TelegramOidcError(
      (await readDetail(response)) || `Telegram sign-in failed (${response.status})`,
      response.status
    );
  }
  const data = await response.json();
  if (data?.status !== 'success') {
    throw new TelegramOidcError(
      data?.message || data?.detail || 'Telegram sign-in failed',
      response.status
    );
  }
  return {
    status: 'success',
    message: data.message,
    api_key: data.api_key,
    user_id: data.user_id,
    is_new_user: data.is_new_user,
    telegram_data: data.telegram_data,
  };
};

/**
 * Attach a Telegram identity to the signed-in account. Never the sign-in route:
 * that OPENS the account a taken id belongs to. Starts no session and returns no
 * key. The session cookie is the credential; `expectedUserId` is the account the
 * page rendered. Refusals carry `.status` and `.detail` (401 session or refused
 * token, 409 taken / already_linked_self / account_mismatch, 503 unavailable).
 */
export const telegramOidcLink = async (
  idToken: string,
  expectedUserId: number
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/auth/telegram/oidc/link`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id_token: idToken, expected_user_id: expectedUserId }),
  });
  if (!response.ok) {
    const detail = await readDetail(response);
    throw new TelegramOidcError(
      detail || `Telegram link failed (${response.status})`,
      response.status,
      detail
    );
  }
};
